import { Highlighter, MapPin, Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ToolCardActionsModel } from '@/features/agent/tools/useToolCardActions'
import type { ToolActionHandlers, ToolActionKind } from './chat-tool-actions'

interface MapCardActionsProps {
  /** Resolved actions from `useToolCardActions`. Rendered only when non-null. */
  model: ToolCardActionsModel
  handlers: ToolActionHandlers
}

// Map-flavoured labels/icons for the graph-action matrix. Same decision logic
// as ToolCardActions (resolved upstream by useToolCardActions), but the CTA
// navigates to the map view and reads "… on map" instead of "… on graph".
const MAP_ICONS: Partial<Record<ToolActionKind, typeof Plus>> = {
  showOnGraph: MapPin,
  showSeparately: MapPin,
  addToGraph: Plus,
  highlight: Highlighter,
}

const MAP_LABEL_KEYS: Partial<Record<ToolActionKind, string>> = {
  showOnGraph: 'chat.toolActions.showOnMap',
  showSeparately: 'chat.toolActions.showOnMap',
  addToGraph: 'chat.toolActions.addToMap',
  highlight: 'chat.toolActions.highlightOnMap',
}

/**
 * Footer action for map-oriented tool-result cards — pure presentation.
 * Renders the resolved primary action with map-specific label/icon; the
 * primary-vs-secondary decision is made upstream in `useToolCardActions`.
 */
export function MapCardActions({ model, handlers }: MapCardActionsProps) {
  const { t } = useTranslation()
  const { primary } = model

  const PrimaryIcon = MAP_ICONS[primary] ?? MapPin
  const primaryLabel = t(MAP_LABEL_KEYS[primary] ?? 'chat.toolActions.showOnMap')

  return (
    <div className="flex items-center justify-end">
      <button
        type="button"
        onClick={handlers[primary]}
        className="inline-flex h-7 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-md bg-primary px-3 text-[11px] font-medium text-primary-foreground shadow-sm transition hover:bg-primary/90 max-md:h-9 max-md:text-xs"
      >
        <PrimaryIcon className="size-3.5" />
        {primaryLabel}
      </button>
    </div>
  )
}
